import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TemplateDocument } from '@template-filler/core';
import { DocumentView } from '../render/DocumentView.js';

interface FilledResultProps {
  title: string;
  doc: TemplateDocument;
  onBack: () => void;
}

export function FilledResult({ title, doc, onBack }: FilledResultProps): JSX.Element {
  const navigate = useNavigate();
  const documentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | undefined>();

  // FR-014b: the result is never stored, so leaving this page destroys it. The
  // user must be warned before that happens, or they lose work with no way back.
  useEffect(() => {
    if (copied) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [copied]);

  async function copy(): Promise<void> {
    const node = documentRef.current;
    if (!node) return;

    try {
      // Both flavours: HTML so formatting survives a paste into a word
      // processor (FR-014a), plain text so it still works in editors that take
      // no markup.
      const html = node.innerHTML;
      const text = node.innerText;

      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }

      setCopied(true);
      setCopyError(undefined);
    } catch {
      setCopyError('Could not copy automatically. Select the document and copy it manually.');
    }
  }

  function leave(destination: () => void): void {
    if (
      copied ||
      window.confirm(
        'This completed document is not saved anywhere. If you leave without copying it, it is gone. Leave anyway?',
      )
    ) {
      destination();
    }
  }

  return (
    <main className="page">
      <header className="page-header">
        <h1>{title}</h1>
        <button type="button" className="btn-ghost" onClick={() => leave(onBack)}>
          Back to the form
        </button>
      </header>

      <div className="banner" role="note">
        This document is not saved. Copy it now — closing this page discards it.
      </div>

      <div className="document-frame" ref={documentRef}>
        <DocumentView doc={doc} />
      </div>

      <div className="page-actions">
        <button type="button" className="btn-primary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy to clipboard'}
        </button>
        <button type="button" className="btn-ghost" onClick={() => leave(() => navigate('/'))}>
          Back to library
        </button>
      </div>

      {copied ? <p role="status">Copied. Paste it wherever it needs to go.</p> : null}
      {copyError ? (
        <p className="error" role="alert">
          {copyError}
        </p>
      ) : null}
    </main>
  );
}
